import { useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router';

import { paths } from '@/app/router/paths';
import { Button, Field, Input } from '@/design-system';
import { authApi, authProblem } from '@/entities/auth';

import { AuthError, AuthHeading, AuthLayout, AuthLink, AuthNotice } from './AuthLayout';
import { authCopy } from './copy';
import { validateEmail } from './validation';

/**
 * Wachtwoord vergeten.
 *
 * `POST /auth/forgot-password` antwoordt altijd hetzelfde, ook voor een adres
 * dat niet bestaat - dus dit scherm ook. Alleen geen verbinding is een melding
 * die iets anders zegt, want dan is er echt niets verstuurd.
 */
export function ForgotPasswordPage() {
    const [search] = useSearchParams();
    const [email, setEmail] = useState(search.get('email') ?? '');
    const [emailError, setEmailError] = useState<string | undefined>();
    const [offline, setOffline] = useState(false);
    const [sent, setSent] = useState(false);
    const [busy, setBusy] = useState(false);

    async function handleSubmit(event: FormEvent) {
        event.preventDefault();

        const error = validateEmail(email);

        setEmailError(error);
        setOffline(false);

        if (error) {
            return;
        }

        setBusy(true);

        try {
            await authApi.forgotPassword(email);
            setSent(true);
        } catch (caught) {
            /* Bewust dezelfde bevestiging: een fout mag niet verklappen of het account bestaat. */
            if (authProblem(caught) === 'offline') {
                setOffline(true);
            } else {
                setSent(true);
            }
        } finally {
            setBusy(false);
        }
    }

    const loginLink = email.trim()
        ? `${paths.login}?email=${encodeURIComponent(email.trim())}`
        : paths.login;

    return (
        <AuthLayout>
            <AuthHeading
                title={authCopy.forgotTitle}
                body={sent ? undefined : authCopy.forgotBody}
            />

            {sent ? (
                <>
                    <AuthNotice>{authCopy.forgotSent}</AuthNotice>
                    <div className="flex justify-start">
                        <AuthLink to={loginLink}>{authCopy.backToLogin}</AuthLink>
                    </div>
                </>
            ) : (
                <form noValidate className="flex flex-col gap-3" onSubmit={handleSubmit}>
                    <Field label={authCopy.email} labelCase="caps" size="lg" error={emailError}>
                        <Input
                            type="email"
                            inputMode="email"
                            autoComplete="username"
                            value={email}
                            onChange={(event) => setEmail(event.target.value)}
                        />
                    </Field>

                    {offline ? <AuthError tone="offline">{authCopy.offline}</AuthError> : null}

                    <Button type="submit" variant="primary" size="lg" fullWidth disabled={busy}>
                        {busy ? authCopy.forgotBusy : authCopy.forgotSubmit}
                    </Button>

                    <div className="flex justify-start">
                        <AuthLink to={loginLink}>{authCopy.backToLogin}</AuthLink>
                    </div>
                </form>
            )}
        </AuthLayout>
    );
}
